import React, { useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Button, UncontrolledTooltip, Input } from "reactstrap";
import { InputSwitch } from "primereact/inputswitch";
import {
    Columns,
    LazyTableState,
} from "src/models/components/dataTableWrapperModel";
import DataTableWrapper from "src/components/Grid/DataTableWrapper";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "src/components/Common/ReactToaster";
import FeatherIcon from "feather-icons-react";
import FilterIcon from "src/components/Common/CustomFilterandSortIcon/FilterIcon";
import SortIcon from "src/components/Common/CustomFilterandSortIcon/SortIcon";
import TooltipMEL from "src/components/UI/Tooltip";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { RootReducerState } from "src/store/reducers";
import {
    getDealerLastUpdatedList,
    getDealerDropdownList,
    clearDealerLastUpdatedData,
    clearDealerLastUpdatedMessage,
    getEmployeeDropdownList,
    getManufacturerDropdownList,
} from "src/store/dealerLastUpdated/actions";
import { DealerLastUpdatedItem } from "src/store/dealerLastUpdated/reducer";
import { formatDateMEL, formatDateTimeMEL } from "src/helpers/common_helpers";
import ExportFile from "src/components/Common/ExcelPdf Export/ExportFile";
import CustomizeTable from "src/components/Common/CustomizeTable/CustomizeTable";
import { customizeTableColumns } from "src/models/components/CustomizeTableColumns";
import ValidationPopup from "src/components/Common/ValidationMessage/ValidationPopup";
import { API } from "src/util/api";
import { saveAsExcelFile } from "src/helpers/common_helpers";
import { ColumnFilterElementTemplateOptions } from "primereact/column";
import { Link } from "react-router-dom";
import { ROUTER } from "src/constants/routes";

const dealerlastupdatedListData = (data: any) => {
    if (!data) return [];
    return data.map((item: any) => {
        return {
            ...item,
            DealerId: item.dealerName || '',
            SupplierId: item.supplierName || '',
            JobConveterGuid: item.employeeName ? item.employeeName : ""
        }
    })
}

export default function DealerLastUpdatedList() {
    const dispatch = useDispatch();
    const { showToast } = useToast();
    const dt = useRef<DataTable<any>>(null);

    // Selectors
    const { values, totalRecords } = useSelector(
        (state: RootReducerState) => state.DealerLastUpdatedReducer.dealerLastUpdatedList
    );
    const { profile } = useSelector((state: RootReducerState) => state.profilesReducer);
    let currentUserId = JSON.parse(localStorage.getItem('authUser') || "{}")?.uid

    const modifiedValues = useMemo(() => {
        return dealerlastupdatedListData(values);
    }, [values]);

    const { error, message, loading, dealerDropdownList, paginatorCount, employeeDropdownList, manufacturerDropdownList } = useSelector(
        (state: RootReducerState) => state.DealerLastUpdatedReducer
    );

    // State
    const [visible, setVisible] = useState(false);
    const [pdfLoading, setPdfLoading] = useState<boolean>(false);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [showCustomizeTable, setShowCustomizeTable] = useState(false);
    const [showMyJob, setShowMyJob] = useState(false);
    // Filter details
    const filterDetails: DataTableFilterMeta = {
        DealerName: { value: "", matchMode: "contains" },
        jobNumber: { value: "", matchMode: "contains" },
        lastUpdate: { value: null, matchMode: "contains" },
        fromDate: { value: null, matchMode: "equals" },
        toDate: { value: null, matchMode: "equals" },
        DealerId: { value: null, matchMode: "equals" },
        SupplierId: { value: null, matchMode: "equals" },
        JobConveterGuid: { value: "", matchMode: "equals" },
    };

    // Initial lazy state
    const initialLazyState: LazyTableState = {
        first: 0,
        rows: 10,
        page: 1,
        sortField: "lastUpdate",
        sortOrder: -1,
        filters: filterDetails,
    };

    const [lazyState, setLazyState] = useState(initialLazyState);

    // Dealer dropdown options - must be at component top level, not inside nested functions
    const dealerOptions = useMemo(() => {
        return dealerDropdownList?.map((dealer: any) => ({
            label: dealer.name,
            value: dealer.userId,
        }));
    }, [dealerDropdownList]);

    const employeeOptions = useMemo(() => {
        return employeeDropdownList?.map((dealer: any) => ({
            label: dealer.name,
            value: dealer.userId,
        }));
    }, [employeeDropdownList]);

    const manufacturerOptions = useMemo(() => {
        return manufacturerDropdownList?.map((item: any) => ({
            label: item.name,
            value: item.userId,
        }));
    }, [manufacturerDropdownList]);

    const dealerFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return (
            <Dropdown
                options={dealerOptions}
                name="buyingCurrency"
                optionLabel="label"
                onChange={(e: DropdownChangeEvent) => {
                    options.filterCallback(e.value, options.index);
                    return {};
                }}
                required={true}
                value={options.value}
                filter
                itemTemplate={(option: any) => (
                    <div
                        title={option.label}
                        style={{ display: 'flex', alignItems: 'center' }}
                    >
                        {option.label}
                    </div>
                )}
            />
        );
    };

    const supplierFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return (
            <Dropdown
                options={manufacturerOptions}
                name="buyingCurrency"
                optionLabel="label"
                onChange={(e: DropdownChangeEvent) => {
                    options.filterCallback(e.value, options.index);
                    return {};
                }}
                required={true}
                value={options.value}
                filter
                itemTemplate={(option: any) => (
                    <div
                        title={option.label}
                        style={{ display: 'flex', alignItems: 'center' }}
                    >
                        {option.label}
                    </div>
                )}
            />
        );
    };

    const employeeFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return (
            <Dropdown
                options={employeeOptions}
                name="buyingCurrency"
                optionLabel="label"
                onChange={(e: DropdownChangeEvent) => {
                    options.filterCallback(e.value, options.index);
                    return {};
                }}
                required={true}
                value={options.value}
                filter
                itemTemplate={(option: any) => (
                    <div
                        title={option.label}
                        style={{ display: 'flex', alignItems: 'center' }}
                    >
                        {option.label}
                    </div>
                )}
            />
        );
    };

    const dateFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return (<>
            <label htmlFor="">Start Date</label>
            <Input type="date"
                required
                name={options?.value?.startDate}
                value={options?.value?.startDate || ""}
                max={options?.value?.endDate ? options?.value?.endDate : ""}
                onChange={(e: any) => {
                    options;
                    let modifiedDate: any = options.value || { startDate: "", endDate: "" };
                    modifiedDate['startDate'] = e.target.value;
                    options.filterCallback(modifiedDate, options.index);
                }}
            />
            <br />
            <label htmlFor="">End Date</label>
            <Input type="date"
                required
                name={options?.value?.endDate}
                value={options?.value?.endDate || ""}
                min={options?.value?.startDate ? options?.value?.startDate : ""}
                onChange={(e: any) => {
                    options;
                    let modifiedDate: any = options.value || { startDate: "", endDate: "" };
                    modifiedDate['endDate'] = e.target.value;
                    options.filterCallback(modifiedDate, options.index);
                }}
            />
        </>);
    };

    const jobNumberTemplate = (rowData: any) => {
        if (rowData?.jobId) {
            return (
                <Link to={ROUTER.JOB_SHEETS_VIEW + "/" + rowData.jobId}>{rowData.jobNumber ? rowData.jobNumber : <div className="empty-number-jobeheet"><span></span><span></span><span></span></div>}</Link>
            );
        } else {
            return rowData.jobNumber || '';
        }
    }

    // Columns definition
    const columns: Columns[] = [
        {
            field: "DealerId",
            sortable: true,
            header: "Dealer Name",
            filter: true,
            showFilterMenu: true,
            headerStyle: { whiteSpace: "nowrap", minWidth: "200px" },
            isDefault: true,
            isStatic: true,
            filterElement: dealerFilterTemplate
        },
        {
            field: "SupplierId",
            sortable: false,
            header: "Supplier Name",
            filter: true,
            showFilterMenu: true,
            headerStyle: { whiteSpace: "nowrap", minWidth: "200px" },
            isDefault: true,
            isStatic: true,
            filterElement: supplierFilterTemplate
        },
        {
            field: "JobConveterGuid",
            sortable: false,
            header: "Employee Name",
            filter: true,
            showFilterMenu: true,
            headerStyle: { whiteSpace: "nowrap", minWidth: "200px" },
            isDefault: true,
            isStatic: true,
            filterElement: employeeFilterTemplate
        },
        {
            field: "jobNumber",
            sortable: true,
            header: "Job Number",
            filter: true,
            showFilterMenu: true,
            body: (rowData: any) => jobNumberTemplate(rowData),
            headerStyle: { whiteSpace: "nowrap", minWidth: "150px" },
            isDefault: true,
            isStatic: false,
        },
        {
            field: "lastUpdate",
            sortable: true,
            header: "Last Update",
            filter: true,
            showFilterMenu: true,
            body: (rowData: DealerLastUpdatedItem) => formatDateTimeMEL(rowData.lastUpdate),
            headerStyle: { whiteSpace: "nowrap", minWidth: "180px" },
            isDefault: true,
            isStatic: false,
            filterElement: dateFilterTemplate
        },
        // {
        //     field: "creationTime",
        //     sortable: true,
        //     header: "Creation Time",
        //     filter: false,
        //     showFilterMenu: false,
        //     body: (rowData: DealerLastUpdatedItem) => formatDateTimeMEL(rowData.creationTime),
        //     headerStyle: { whiteSpace: "nowrap", minWidth: "180px" },
        //     isDefault: true,
        //     isStatic: false,
        // },
    ];

    const [visibleColumns, setVisibleColumns] = useState(
        columns.filter((eachColumn: Columns) => eachColumn.isDefault)
    );

    useEffect(() => {
        setVisibleColumns(columns.filter((eachColumn: Columns) => eachColumn.isDefault))
    }, [dealerDropdownList, employeeDropdownList, manufacturerOptions])

    // Effects
    useEffect(() => {
        dispatch(getDealerDropdownList());
        dispatch(getEmployeeDropdownList());
        dispatch(getManufacturerDropdownList());
        return () => {
            dispatch(clearDealerLastUpdatedData());
        };
    }, []);

    useEffect(() => {
        if (error) {
            setVisible(true);
        }
        if (message) {
            showToast(message, { type: "success" });
            dispatch(clearDealerLastUpdatedMessage());
        }
    }, [error, message]);

    useEffect(() => {
        callbackFunction(lazyState);
    }, [lazyState, profile]);

    useEffect(() => {
        setLazyState((prev: any) => ({
            ...prev,
            filters: {
                ...prev.filters,
                JobConveterGuid: { value: showMyJob ? currentUserId : "", matchMode: "equals" }
            }
        }));
    }, [showMyJob]);

    useEffect(() => {
        const preloader: any = document.getElementById("preloader");
        if (loading || pdfLoading) {
            preloader.style.display = "block";
        } else {
            preloader.style.display = "none";
        }
    }, [loading, pdfLoading]);

    // Callbacks
    const callbackFunction = (lazyState: LazyTableState) => {
        let modifiedLazyState = {
            ...lazyState,
            filters: {
                ...lazyState.filters,
                lastUpdate: { value: "" },
            }
        }
        dispatch(getDealerLastUpdatedList(modifiedLazyState));
    };

    // Handlers
    const hidePopup = () => {
        setVisible(false);
        dispatch(clearDealerLastUpdatedMessage());
    };

    const onPage = (event: any) => {
        setLazyState(event);
    };

    const onSort = (event: any) => {
        setLazyState(event);
    };

    const onFilter = (event: any) => {
        event["first"] = 0;
        event['filters']['fromDate']['value'] = event?.filters?.lastUpdate?.value?.startDate ? event?.filters?.lastUpdate?.value?.startDate : '';
        event['filters']['toDate']['value'] = event?.filters?.lastUpdate?.value?.endDate ? event?.filters?.lastUpdate?.value?.endDate : '';
        setLazyState(event);
    };

    const clearFilter = () => {
        setShowMyJob(false);
        setLazyState(initialLazyState);
    };

    const customizeTableClickHandler = () => {
        setShowCustomizeTable(true);
    };

    const customizeTableCancelHandler = () => {
        setShowCustomizeTable(false);
    };

    const customizeColumnSubmitHandler = (customizeColumnObject: any[]) => {
        let orderedSelectedColumns = columns.filter((col) =>
            customizeColumnObject.some((sCol: any) => sCol === col.field)
        );
        setVisibleColumns(orderedSelectedColumns);
        setShowCustomizeTable(false);
    };

    const toggleTooltip = () => setTooltipOpen((prev) => !prev);

    // Export functions
    const changeColumnDetails = (values: any[]) => {
        return values?.map((eachItem) => {
            return {
                "Dealer Name": eachItem.dealerName,
                "Supplier Name": eachItem.supplierName,
                "Job Number": eachItem.jobNumber,
                "Last Update": eachItem.lastUpdate ? formatDateTimeMEL(eachItem.lastUpdate) : "",
                // "Creation Time": eachItem.creationTime ? formatDateTimeMEL(eachItem.creationTime) : "",
            };
        });
    };

    const saveDataAsExcel = (values: any[], fileName: string) => {
        const modifiedColumnDetails = changeColumnDetails(values);
        import("xlsx").then((xlsx) => {
            const worksheet = xlsx.utils.json_to_sheet(modifiedColumnDetails);
            const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
            const excelBuffer = xlsx.write(workbook, {
                bookType: "xlsx",
                type: "array",
            });
            saveAsExcelFile(excelBuffer, fileName);
        });
    };

    const saveDataAsPdf = (values: any[], columns: any[], fileName: string) => {
        const destructuredData = values?.map((data) => ({
            SupplierId: data.supplierName,
            jobNumber: data.jobNumber,
            lastUpdate: data.lastUpdate ? formatDateTimeMEL(data.lastUpdate) : "",
            // creationTime: data.creationTime ? formatDateTimeMEL(data.creationTime) : "",
        }));

        import("jspdf").then((jsPDF) => {
            import("jspdf-autotable").then(() => {
                const exportColumns = columns?.map((col) => ({
                    title: col.header,
                    dataKey: col.field,
                    styles: {
                        cellPadding: 2,
                        halign: "center",
                    },
                }));
                const doc = new (jsPDF as any).default({
                    orientation: "landscape",
                    format: "a4",
                });
                doc.autoTable(exportColumns, destructuredData);
                doc.save(`${fileName}.pdf`);
            });
        });
    };

    const fetchData = async (isPdf: boolean) => {
        let state: any = {
            ...lazyState,
            rows: totalRecords > 5000 ? 5000 : totalRecords < 1 ? 1 : totalRecords,
            first: 0,
        };

        try {
            setPdfLoading(true);
            const response = await API.getDealerLastUpdatedListAPI(state);
            if (!response) {
                throw new Error("Network response was not ok");
            }
            const resp = response?.data?.items;
            if (isPdf) {
                saveDataAsPdf(resp, columns, "dealer-last-updated");
            } else {
                saveDataAsExcel(resp, "dealer-last-updated");
            }
            setPdfLoading(false);
        } catch (error: any) {
            showToast(error?.message, { type: "error" });
            setPdfLoading(false);
        }
    };

    const csvClickHandler = () => {
        fetchData(false);
    };

    const pdfClickHandler = () => {
        fetchData(true);
    };

    // Dialog container
    const dialogContainer = (error: any) => {
        return <ValidationPopup error={error} visible={visible} onHide={hidePopup} />;
    };

    // Customize table columns
    const customizeTableColumnsData: customizeTableColumns[] = columns.map(
        (eachColumns: Columns) => {
            return {
                field: eachColumns.field,
                value: visibleColumns.some((sCol: any) => sCol.field === eachColumns.field),
                isDisable: eachColumns.isStatic ?? false,
                header: eachColumns.header,
            };
        }
    );



    // Header
    const header = (
        <div className="table-header-container">
            <div>Dealer / Supplier Last Updated List</div>
            <div className="flex-grow-1"></div>
            <div className="table-header-container-item">
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div className='me-1 d-flex gap-1 align-items-center'>
                        <div>Show My Job</div> &nbsp;
                        <InputSwitch checked={showMyJob} onChange={(e) => setShowMyJob(e.value)} />
                    </div>
                    <ExportFile
                        exportExcel={csvClickHandler}
                        exportPdf={pdfClickHandler}
                        clearFilter={clearFilter}
                        isFilter={true}
                    />
                    <Button
                        type="button"
                        className="customize-table-button"
                        id="ScheduleUpdateTooltip"
                        onMouseEnter={() => setTooltipOpen(true)}
                        onMouseLeave={() => setTooltipOpen(false)}
                        data-btn="btn"
                        onClick={customizeTableClickHandler}
                    >
                        <FeatherIcon icon="sliders" className="" />
                    </Button>
                </div>
                <UncontrolledTooltip
                    placement="bottom"
                    target="ScheduleUpdateTooltip"
                    isOpen={tooltipOpen}
                    toggle={toggleTooltip}
                    innerClassName="tooltip-color"
                >
                    Choose fields you want to see in the table
                </UncontrolledTooltip>
            </div>
        </div>
    );

    return (
        <>
            <div className="page-content">
                {visible && dialogContainer(error)}
                {showCustomizeTable && (
                    <CustomizeTable
                        onCancelHandler={customizeTableCancelHandler}
                        onCustomizeColumnSubmitHandler={customizeColumnSubmitHandler}
                        columns={customizeTableColumnsData}
                    />
                )}
                <DataTableWrapper
                    ref={dt}
                    columnDetails={visibleColumns}
                    header={header}
                    value={modifiedValues}
                    sortIcon={(setOrder) => <SortIcon order={setOrder} />}
                    filterIcon={FilterIcon}
                    lazy
                    paginator={true}
                    stripedRows={true}
                    rowHover={true}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    totalRecords={totalRecords}
                    emptyMessage="No details found."
                    paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                    currentPageReportTemplate="Displaying {last} records of {totalRecords} in total"
                    first={lazyState.first}
                    onSort={onSort}
                    sortField={lazyState.sortField}
                    sortOrder={lazyState.sortOrder}
                    onFilter={onFilter}
                    filters={lazyState.filters}
                    onPage={onPage}
                    rows={lazyState.rows}
                ></DataTableWrapper>
            </div>
        </>
    );
}
